/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import {GenericObject} from '@ion/core/src/types/entities';
import {DATE_RANGE_FORMAT, dateFormatter, getCurrentDate} from '@ion/core/src/utils/DateUtil';
import {DraggableItems} from '@ion/dls/src/components/draggable/DraggableListItem';
import {PageState} from '@/types/pages';
import Features from '../../security/Features';
import {
  DashboardTab,
  defaultWidgetList,
  DriverDashboardFilters,
  DriverDashboardTab,
  INITIALIZE_DASHBOARD,
  SET_ADDED_WIDGETS,
  SET_DRIVER_DASHBOARD_FILTERS,
  SET_DRIVER_DASHBOARD_SELECTED_TAB,
  SET_SELECTED_DASHBOARD_TAB,
  SET_SHOW_EDIT_WIDGETS,
  SET_SISENSE_IFRAME_WIDGET,
  SET_SISENSE_SDK_WIDGET
} from '../actions/dashboard';
import {dashboardPersistence} from '../helpers';

// defines the type definitions for dashboard actions ...
export interface DashboardAction {
  selectedTab: DashboardTab;
};

export interface DriverDashboardFiltersAction {
  filters: DriverDashboardFilters;
};

export interface DriverDashboardTabAction {
  selectedTab: DriverDashboardTab;
};

// defines the type definitions for widget type...
export interface DashboardWidget {
  addedWidgets: DraggableItems;
  availableWidgets: DraggableItems;
};

// defines the type definitions for dashboard reducer ...
export interface DashboardState extends PageState {
  isInitialized: boolean;
  showEdit: boolean;
  selectedTab: DashboardTab | null;
  addedWidgets: GenericObject;
  iFrameWidgets: DashboardWidget;
  sdkWidgets: DashboardWidget;
  driverDashboardFilters: DriverDashboardFilters;
  driverDashboardSelectedTab: DriverDashboardTab;
};

// default sisense widget order...
export const defaultSisenseWidgetList = [
  {dashboardId: '61ae95d512b1a20037dbb6bd', id: '61b0ffb27c9632003638dcbc', primaryText: 'dashboard:sisense.iframe.widgets.violationsWidget'},
  {dashboardId: '61ae95d512b1a20037dbb6bd', id: '61b1020d7c9632003638dcc9', primaryText: 'dashboard:sisense.iframe.widgets.violationsByDriverWidget'},
  {dashboardId: '61ae95d512b1a20037dbb6bd', id: '61b10fc37c9632003638dd98', primaryText: 'dashboard:sisense.iframe.widgets.violationsByAccounts'},
  {dashboardId: '61ae95d512b1a20037dbb6bd', id: '61b13ed87c9632003638de74', primaryText: 'dashboard:sisense.iframe.widgets.fleetScore'}
];

export const selectedTab = (): DashboardTab | undefined => {
  if (Features.hasDevices()) {
    return DashboardTab.DEVICES;
  } else if (Features.hasSafety()) {
    return DashboardTab.SAFETY;
  } else if (Features.hasSafetyBasic()) {
    return DashboardTab.SAFETY_BASIC;
  } else if (Features.hasUtilization()) {
    return DashboardTab.UTILIZATION;
  } else if (Features.hasDriverDashboardFeature()) {
    return DashboardTab.DRIVER;
  }
};

export const getDefaultDriverDashboardFilters = (): DriverDashboardFilters => {
  const endDate = new Date(getCurrentDate());
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - 7);

  return {
    startDate : dateFormatter(startDate, DATE_RANGE_FORMAT),
    endDate   : dateFormatter(endDate, DATE_RANGE_FORMAT),
    assets    : []
  };
};

// defines the initial state for the reducer ...
export const initialState: DashboardState = {
  isInitialized              : false,
  showEdit                   : false,
  selectedTab                : null,
  addedWidgets               : {},
  driverDashboardFilters     : getDefaultDriverDashboardFilters(),
  driverDashboardSelectedTab : DriverDashboardTab.EVENTS,
  iFrameWidgets              : {
    addedWidgets     : defaultSisenseWidgetList,
    availableWidgets : []
  },
  sdkWidgets : {
    addedWidgets     : defaultSisenseWidgetList,
    availableWidgets : []
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_DASHBOARD] : draft => {
    draft.isInitialized = true;
    draft.addedWidgets  = dashboardPersistence() || defaultWidgetList;
    draft.selectedTab   = draft.selectedTab || selectedTab();
  },
  [SET_SELECTED_DASHBOARD_TAB] : (
    draft: DashboardState, {selectedTab}: DashboardAction) => {
    draft.selectedTab = selectedTab;
  },
  [SET_ADDED_WIDGETS] : (
    draft: DashboardState, {widgetList}) => {
    draft.addedWidgets = widgetList;
    dashboardPersistence(widgetList, true);
  },
  [SET_SISENSE_IFRAME_WIDGET] : (draft: DashboardState, {widgetList}) => {
    draft.iFrameWidgets = widgetList;
  },
  [SET_SISENSE_SDK_WIDGET] : (draft: DashboardState, {widgetList}) => {
    draft.sdkWidgets = widgetList;
  },
  [SET_DRIVER_DASHBOARD_FILTERS] : (
    draft: DashboardState, {filters}: DriverDashboardFiltersAction) => {
    draft.driverDashboardFilters = filters;
  },
  [SET_DRIVER_DASHBOARD_SELECTED_TAB] : (
    draft: DashboardState, {selectedTab}: DriverDashboardTabAction) => {
    draft.driverDashboardSelectedTab = selectedTab;
  },
  [SET_SHOW_EDIT_WIDGETS] : (draft: DashboardState, {showEdit}) => {
    draft.showEdit = !showEdit;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: PageState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
